import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { supabaseRealtimeManager } from '../services/supabaseRealtimeManager';

// Types
export interface DataState<T = any> {
  data: T | null;
  loading: boolean;
  error: string | null;
  lastUpdated: Date | null;
}

export interface DashboardDataContextType {
  // Common data
  campaigns: DataState<any[]>;
  applications: DataState<any[]>;
  notifications: DataState<any[]>;
  messages: DataState<any[]>;
  
  // Role-specific data
  brandData: {
    metrics: DataState<any>;
    featuredCreators: DataState<any[]>;
    pendingCounts: { applications: number; invitations: number };
  };
  creatorData: {
    metrics: DataState<any>;
    availableCampaigns: DataState<any[]>;
    invitations: DataState<any[]>;
    earnings: DataState<any>;
  };
  
  // Actions
  refreshAll: () => Promise<void>;
  refreshCampaigns: () => Promise<void>;
  refreshApplications: () => Promise<void>;
  refreshNotifications: () => Promise<void>;
  
  // Utils
  isLoading: boolean;
  hasError: boolean;
  errorMessage: string | null;
  lastRefresh: Date | null;
}

const initialDataState: DataState = {
  data: null,
  loading: false,
  error: null,
  lastUpdated: null,
};

const DashboardDataContext = createContext<DashboardDataContextType | undefined>(undefined);

export const useDashboardData = (): DashboardDataContextType => {
  const context = useContext(DashboardDataContext);
  if (!context) {
    throw new Error('useDashboardData must be used within DashboardDataProvider');
  }
  return context;
};

interface DashboardDataProviderProps {
  children: ReactNode;
}

export const DashboardDataProvider: React.FC<DashboardDataProviderProps> = ({ children }) => {
  const { user, userProfile } = useAuth();
  
  // Common state
  const [campaigns, setCampaigns] = useState<DataState<any[]>>(initialDataState);
  const [applications, setApplications] = useState<DataState<any[]>>(initialDataState);
  const [notifications, setNotifications] = useState<DataState<any[]>>(initialDataState);
  const [messages, setMessages] = useState<DataState<any[]>>(initialDataState);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  
  // Brand-specific state
  const [brandMetrics, setBrandMetrics] = useState<DataState<any>>(initialDataState);
  const [featuredCreators, setFeaturedCreators] = useState<DataState<any[]>>(initialDataState);
  const [pendingCounts, setPendingCounts] = useState({ applications: 0, invitations: 0 });
  
  // Creator-specific state
  const [creatorMetrics, setCreatorMetrics] = useState<DataState<any>>(initialDataState);
  const [availableCampaigns, setAvailableCampaigns] = useState<DataState<any[]>>(initialDataState);
  const [invitations, setInvitations] = useState<DataState<any[]>>(initialDataState);
  const [earnings, setEarnings] = useState<DataState<any>>(initialDataState);

  // Refresh campaigns
  const refreshCampaigns = useCallback(async () => {
    if (!user?.id || !userProfile) return;
    
    setCampaigns(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      let campaignsData: any[] = [];
      
      switch (userProfile.role) {
        case 'brand':
          const { brandService } = await import('../services/brandService');
          campaignsData = await brandService.listBrandCampaigns(user.id);
          break;
        case 'creator':
          const { creatorService } = await import('../services/creatorService');
          campaignsData = await creatorService.getCreatorActiveCampaigns(user.id);
          break;
      }
      
      setCampaigns({
        data: campaignsData,
        loading: false,
        error: null,
        lastUpdated: new Date(),
      });
    } catch (error: any) {
      console.error('Error refreshing campaigns:', error);
      setCampaigns(prev => ({
        ...prev,
        loading: false,
        error: error.message || 'Failed to load campaigns',
      }));
    }
  }, [user?.id, userProfile]);

  // Refresh applications
  const refreshApplications = useCallback(async () => {
    if (!user?.id || !userProfile) return;
    
    setApplications(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      let applicationsData: any[] = [];
      
      switch (userProfile.role) {
        case 'brand':
          const { brandService } = await import('../services/brandService');
          applicationsData = await brandService.getBrandApplications(user.id);
          break;
        case 'creator':
          const { campaignApplicationService } = await import('../services/campaignApplicationService');
          applicationsData = await campaignApplicationService.getUserApplications(user.id);
          break;
      }
      
      setApplications({
        data: applicationsData,
        loading: false,
        error: null,
        lastUpdated: new Date(),
      });
    } catch (error: any) {
      console.error('Error refreshing applications:', error);
      setApplications(prev => ({
        ...prev,
        loading: false,
        error: error.message || 'Failed to load applications',
      }));
    }
  }, [user?.id, userProfile]);

  // Refresh notifications
  const refreshNotifications = useCallback(async () => {
    if (!user?.id) return;
    
    setNotifications(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const { notificationService } = await import('../services/notificationService');
      const notifs = await notificationService.getNotifications(user.id, 20);
      
      setNotifications({
        data: notifs,
        loading: false,
        error: null,
        lastUpdated: new Date(),
      });
    } catch (error: any) {
      console.error('Error refreshing notifications:', error);
      setNotifications(prev => ({
        ...prev,
        loading: false,
        error: error.message || 'Failed to load notifications',
      }));
    }
  }, [user?.id]);

  // Refresh role-specific data
  const refreshRoleData = useCallback(async () => {
    if (!user?.id || !userProfile) return;
    
    try {
      switch (userProfile.role) {
        case 'brand':
          // Load brand-specific data
          setBrandMetrics(prev => ({ ...prev, loading: true }));
          setFeaturedCreators(prev => ({ ...prev, loading: true }));
          
          const { brandService } = await import('../services/brandService');
          const { creatorService } = await import('../services/creatorService');
          
          const [metrics, creators, appCount, invCount] = await Promise.all([
            brandService.getBrandMetrics(user.id),
            creatorService.getFeaturedCreators(5),
            brandService.getPendingApplicationsCount(user.id),
            brandService.getBrandInvitationsCount(user.id, 'pending'),
          ]);
          
          setBrandMetrics({
            data: metrics,
            loading: false,
            error: null,
            lastUpdated: new Date(),
          });
          setFeaturedCreators({
            data: creators,
            loading: false,
            error: null,
            lastUpdated: new Date(),
          });
          setPendingCounts({ applications: appCount || 0, invitations: invCount || 0 });
          break;
          
        case 'creator':
          // Load creator-specific data
          setCreatorMetrics(prev => ({ ...prev, loading: true }));
          setAvailableCampaigns(prev => ({ ...prev, loading: true }));
          setInvitations(prev => ({ ...prev, loading: true }));
          
          const { creatorService: cs } = await import('../services/creatorService');
          const { campaignApplicationService } = await import('../services/campaignApplicationService');
          const { invitationService } = await import('../services/invitationService');
          
          const [cMetrics, availCampaigns, invites] = await Promise.all([
            cs.getCreatorMetrics(user.id),
            campaignApplicationService.getAvailableCampaigns(),
            invitationService.getCreatorInvitations(user.id),
          ]);
          
          setCreatorMetrics({
            data: cMetrics,
            loading: false,
            error: null,
            lastUpdated: new Date(),
          });
          setAvailableCampaigns({
            data: availCampaigns,
            loading: false,
            error: null,
            lastUpdated: new Date(),
          });
          setInvitations({
            data: invites,
            loading: false,
            error: null,
            lastUpdated: new Date(),
          });
          break;
      }
    } catch (error: any) {
      console.error('Error refreshing role data:', error);
    }
  }, [user?.id, userProfile]);

  // Refresh all data
  const refreshAll = useCallback(async () => {
    console.log('🔄 Refreshing all dashboard data...');
    
    await Promise.all([
      refreshCampaigns(),
      refreshApplications(),
      refreshNotifications(),
      refreshRoleData(),
    ]);
    
    setLastRefresh(new Date());
    console.log('✅ Dashboard data refresh complete');
  }, [refreshCampaigns, refreshApplications, refreshNotifications, refreshRoleData]);

  // Initial data load
  useEffect(() => {
    if (user?.id && userProfile) {
      refreshAll();
    }
  }, [user?.id, userProfile?.id, userProfile?.role]);

  // Set up real-time subscriptions
  useEffect(() => {
    if (!user?.id || !userProfile) return;
    
    const role = userProfile.role;
    const channelPrefix = `data-${role}-${user.id}`;
    
    // Subscribe to campaign changes
    supabaseRealtimeManager.subscribe({
      channel: `${channelPrefix}-campaigns`,
      event: '*',
      schema: 'public',
      table: 'campaigns',
      callback: () => {
        console.log('📡 Real-time: Campaign update');
        refreshCampaigns();
        refreshRoleData();
      },
    });
    
    // Subscribe to application changes
    supabaseRealtimeManager.subscribe({
      channel: `${channelPrefix}-applications`,
      event: '*',
      schema: 'public',
      table: 'campaign_applications',
      callback: () => {
        console.log('📡 Real-time: Application update');
        refreshApplications();
        refreshRoleData();
      },
    });
    
    // Subscribe to notification changes
    supabaseRealtimeManager.subscribe({
      channel: `${channelPrefix}-notifications`,
      event: '*',
      schema: 'public',
      table: 'notifications',
      filter: `user_id=eq.${user.id}`,
      callback: () => {
        console.log('📡 Real-time: Notification update');
        refreshNotifications();
      },
    });
    
    return () => {
      supabaseRealtimeManager.unsubscribe(`${channelPrefix}-campaigns`);
      supabaseRealtimeManager.unsubscribe(`${channelPrefix}-applications`);
      supabaseRealtimeManager.unsubscribe(`${channelPrefix}-notifications`);
    };
  }, [user?.id, userProfile?.role, refreshCampaigns, refreshApplications, refreshNotifications, refreshRoleData]);

  // Computed values
  const isLoading = campaigns.loading || applications.loading || notifications.loading;
  const hasError = !!(campaigns.error || applications.error || notifications.error);
  const errorMessage = campaigns.error || applications.error || notifications.error || null;

  const value: DashboardDataContextType = {
    campaigns,
    applications,
    notifications,
    messages,
    brandData: {
      metrics: brandMetrics,
      featuredCreators,
      pendingCounts,
    },
    creatorData: {
      metrics: creatorMetrics,
      availableCampaigns,
      invitations,
      earnings,
    },
    refreshAll,
    refreshCampaigns,
    refreshApplications,
    refreshNotifications,
    isLoading,
    hasError,
    errorMessage,
    lastRefresh,
  };

  return (
    <DashboardDataContext.Provider value={value}>
      {children}
    </DashboardDataContext.Provider>
  );
};

export default DashboardDataProvider;
